import { useEffect, useState } from 'react'
import { Link, useParams, useLocation, useSearchParams } from 'react-router-dom'
import { usePublishedArticle, useRecommendedArticles } from '../api/hooks'
import type { NavMethod } from '../api/services/articleService'
import { ArticleRenderer } from '../components/article-viewer/ArticleRenderer'
import PageLoader from '../components/common/PageLoader'
import { metricsApiClient } from '../api/http/wireApiClient'
import { useAuthz } from '../auth/useAuthz'

function RecommendedRow({
  item,
  rank,
}: {
  item: { id: string; title: string; thumbnail?: { presignedUrl?: string; altText?: string } }
  rank: number
}) {
  return (
    <li className="text-sm leading-snug">
      <Link
        to={`/wire/articles/${item.id}`}
        state={{ navMethod: 'recommended' as NavMethod }}
        className="group flex gap-2 text-fg transition-colors hover:text-accent-primary-fg"
      >
        <span className="min-w-5 pt-0.5 text-[11px] font-semibold text-fg-secondary group-hover:text-accent-primary-fg">
          {rank}.
        </span>
        <div className="flex min-w-0 flex-1 items-start gap-2.5">
          {item.thumbnail?.presignedUrl ? (
            <img
              src={item.thumbnail.presignedUrl}
              alt={item.thumbnail.altText ?? ''}
              className="h-12 w-16 shrink-0 rounded border border-line object-cover"
              loading="lazy"
            />
          ) : (
            <div className="flex h-12 w-16 shrink-0 items-center justify-center rounded border border-dashed border-line bg-canvas text-[9px] font-semibold uppercase tracking-wider text-fg-secondary">
              No image
            </div>
          )}
          <span className="min-w-0 underline-offset-2 group-hover:underline">{item.title || 'Untitled article'}</span>
        </div>
      </Link>
    </li>
  )
}

export default function ArticleViewPage() {
  const { id = '' } = useParams<{ id: string }>()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const state = location.state as { navMethod?: NavMethod; pageId?: string } | null

  // In-app navigation passes navMethod via router state; external shared links
  // encode it as ?nav_method=gen_link in the URL.
  const navMethod: NavMethod | undefined =
    state?.navMethod ?? (searchParams.get('nav_method') as NavMethod | null) ?? undefined
  const pageId = state?.pageId ?? undefined
  const { isWorkflowMember } = useAuthz()
  const [adminMode, setAdminMode] = useState(false)

  const { data: article, isPending, isError } = usePublishedArticle(id, { navMethod, pageId })
  const { data: recommended } = useRecommendedArticles()

  const recommendedTopTen = (recommended ?? [])
    .filter((candidate) => candidate.id !== id)
    .slice(0, 10)
  const leftColumn = recommendedTopTen.slice(0, 5)
  const rightColumn = recommendedTopTen.slice(5, 10)

  // Fire collection-tracking heartbeat immediately on load, then every 5 s.
  // scrollYIndex is 0–100 (% of article scrolled). Fire-and-forget — tracking
  // errors must never surface to the reader.
  useEffect(() => {
    if (!article?.id) return

    const fire = () => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight
      const scrollYIndex = scrollable > 0
        ? Math.max(0, Math.min(100, Math.round((window.scrollY / scrollable) * 100)))
        : 100
      metricsApiClient
        .put(`/collection/article/${article.id}`, {
          scrollYIndex,
          clientTimestamp: Date.now(),
        })
        .catch(() => { /* intentionally swallowed */ })
    }

    fire()
    const timer = setInterval(fire, 5000)
    return () => clearInterval(timer)
  }, [article?.id])

  if (isPending) return <PageLoader />

  if (isError || !article) {
    return (
      <div className="flex h-64 items-center justify-center text-sm text-fg-secondary">
        Article not found.
      </div>
    )
  }

  return (
    <>
      <ArticleRenderer
        article={article}
        trackVisualEngagement
        navMethod={navMethod}
        pageId={pageId}
        adminMode={adminMode}
        canToggleAdminMode={isWorkflowMember}
        onToggleAdminMode={isWorkflowMember ? () => setAdminMode(current => !current) : undefined}
      />

      {recommendedTopTen.length > 0 && (
        <section className="mx-auto w-full max-w-5xl px-8 pb-12">
          <div className="mx-auto max-w-3xl border-t border-line pt-8">
            <h2 className="font-zodiak text-lg font-bold text-fg">Recommended Articles</h2>
            <div className="mt-4 grid gap-6 sm:grid-cols-2">
              <ol className="space-y-3">
                {leftColumn.map((item, index) => (
                  <RecommendedRow key={item.id} item={item} rank={index + 1} />
                ))}
              </ol>

              <ol className="space-y-3" start={6}>
                {rightColumn.map((item, index) => (
                  <RecommendedRow key={item.id} item={item} rank={index + 6} />
                ))}
              </ol>
            </div>
          </div>
        </section>
      )}
    </>
  )
}
